import { useEffect, useMemo, useState } from "react";

import { SelectionPill } from "@shared/designSystem/SelectionPill";
import { SelectedSearchInput } from "@shared/designSystem/SelectedSearchInput";
import { CardContainer, HorizontalContainer, VerticalContainer } from "@shared/designSystem/Containers";
import { H1Title, H2Title, Subtitle } from "@shared/designSystem/Titles";
import { SmallText, Text } from "@shared/designSystem/Texts";
import designSystemStyles from "@shared/styles/designSystemStyles";

interface ShowcasePerson {
    id: string;
    name: string;
    role: string;
}

const people: ShowcasePerson[] = [
    { id: "ana", name: "Ana Lopez", role: "People Ops" },
    { id: "bruno", name: "Bruno Perez", role: "Payroll" },
    { id: "carla", name: "Carla Gomez", role: "Operations" },
    { id: "diego", name: "Diego Ruiz", role: "Support" },
    { id: "elena", name: "Elena Castro", role: "Finance" },
    { id: "franco", name: "Franco Silva", role: "Field Lead" },
    { id: "gabriela", name: "Gabriela Torres", role: "Admin" },
    { id: "hector", name: "Hector Molina", role: "Payroll" },
];

const SelectedSearchInputShowcaseScreen = () => {
    const [query, setQuery] = useState("");
    const [debouncedQuery, setDebouncedQuery] = useState("");
    const [results, setResults] = useState<ShowcasePerson[]>([]);
    const [selectedPeople, setSelectedPeople] = useState<ShowcasePerson[]>([people[0]]);
    const [loading, setLoading] = useState(false);
    const [nextStepCount, setNextStepCount] = useState(0);

    const selectedIDs = useMemo(() => new Set(selectedPeople.map((person) => person.id)), [selectedPeople]);

    useEffect(() => {
        if (!debouncedQuery.trim()) {
            setResults([]);
            setLoading(false);
            return;
        }

        setLoading(true);
        const timeoutID = window.setTimeout(() => {
            const normalizedQuery = debouncedQuery.toLowerCase().trim();
            setResults(
                people.filter((person) => {
                    const searchableText = `${person.name} ${person.role}`.toLowerCase();
                    return !selectedIDs.has(person.id) && searchableText.includes(normalizedQuery);
                }),
            );
            setLoading(false);
        }, 450);

        return () => {
            window.clearTimeout(timeoutID);
        };
    }, [debouncedQuery, selectedIDs]);

    const handleSelect = (person: ShowcasePerson) => {
        setSelectedPeople((currentPeople) => {
            if (currentPeople.some((selectedPerson) => selectedPerson.id === person.id)) {
                return currentPeople;
            }

            return [...currentPeople, person];
        });
        setQuery("");
        setDebouncedQuery("");
        setResults([]);
    };

    const handleRemove = (person: ShowcasePerson) => {
        setSelectedPeople((currentPeople) => currentPeople.filter((selectedPerson) => selectedPerson.id !== person.id));
    };

    return (
        <VerticalContainer width="100%" gap={designSystemStyles.gapLg}>
            <VerticalContainer gap={designSystemStyles.gapNone}>
                <H1Title text="SelectedSearchInput Showcase" />
                <Subtitle text="Parent-owned debounced search with removable selected pills." />
            </VerticalContainer>

            <CardContainer>
                <VerticalContainer gap={designSystemStyles.gapSm} alignment="left">
                    <H2Title text="Selection pill" />
                    <HorizontalContainer gap={designSystemStyles.gapSm} justifyContent="flex-start">
                        <SelectionPill label="Payroll" onRemove={() => {}} />
                        <SelectionPill label="Disabled pill" onRemove={() => {}} disabled={true} />
                    </HorizontalContainer>
                </VerticalContainer>
            </CardContainer>

            <CardContainer>
                <VerticalContainer gap={designSystemStyles.gapMd} alignment="left" width="520px">
                    <H2Title text="Selected search input" />
                    <SelectedSearchInput
                        label="People"
                        placeholder="Search by name or role"
                        value={selectedPeople}
                        results={results}
                        query={query}
                        loading={loading}
                        maxVisibleResults={4}
                        onQueryChange={setQuery}
                        onDebouncedQueryChange={setDebouncedQuery}
                        onSelect={handleSelect}
                        onRemove={handleRemove}
                        onEnterPress={() => setNextStepCount((count) => count + 1)}
                        getItemKey={(person) => person.id}
                        getItemLabel={(person) => person.name}
                        renderResult={(person) => (
                            <VerticalContainer gap={designSystemStyles.gapNone} margin="0">
                                <Text text={person.name} />
                                <SmallText text={person.role} color={designSystemStyles.colorTextSecondary} />
                            </VerticalContainer>
                        )}
                    />
                    <SmallText
                        text={`Debounced query: ${debouncedQuery || "none"} | Next-step signal count: ${nextStepCount}`}
                        color={designSystemStyles.colorTextSecondary}
                    />
                </VerticalContainer>
            </CardContainer>

            <CardContainer>
                <VerticalContainer gap={designSystemStyles.gapMd} alignment="left" width="520px">
                    <H2Title text="States" />
                    <SelectedSearchInput
                        label="Disabled"
                        placeholder="Cannot edit"
                        value={[people[1]]}
                        results={[]}
                        query=""
                        disabled={true}
                        onQueryChange={() => {}}
                        onDebouncedQueryChange={() => {}}
                        onSelect={() => {}}
                        onRemove={() => {}}
                        onEnterPress={() => {}}
                        getItemKey={(person) => person.id}
                        getItemLabel={(person) => person.name}
                    />
                    <SelectedSearchInput
                        label="Error"
                        placeholder="Search"
                        value={[]}
                        results={[]}
                        query=""
                        error="Choose at least one person before continuing."
                        onQueryChange={() => {}}
                        onDebouncedQueryChange={() => {}}
                        onSelect={() => {}}
                        onRemove={() => {}}
                        onEnterPress={() => {}}
                        getItemKey={(person: ShowcasePerson) => person.id}
                        getItemLabel={(person: ShowcasePerson) => person.name}
                    />
                </VerticalContainer>
            </CardContainer>
        </VerticalContainer>
    );
};

export default SelectedSearchInputShowcaseScreen;
